import {
  AfterViewInit,
  Component,
  DestroyRef,
  Inject,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, UntypedFormControl } from '@angular/forms';
import { NavigationStart, Router } from '@angular/router';
import { TypedDictionary } from '@core/utils';
import { CountryWithAirports, Region } from '@store/origins/types';
import {
  JbAutocompleteOptionsGroup,
  JbDialogComponent,
  JbDialogContentComponent,
  WINDOW,
} from 'jb-component-library';
import {
  BehaviorSubject,
  combineLatest,
  concat,
  filter,
  fromEvent,
  merge,
  of,
  take,
  takeUntil,
} from 'rxjs';

import { RegionSelectorV2Component } from '../region-selector-v2';

@Component({
  selector: 'dot-region-selector-dialog-v2',
  templateUrl: './region-selector-dialog-v2.component.html',
  styleUrls: ['./region-selector-dialog-v2.component.scss'],
})
export class RegionSelectorV2DialogComponent implements OnInit, AfterViewInit {
  private _destroy = inject(DestroyRef);

  citySelector: any;
  content: any;
  control: UntypedFormControl | AbstractControl;
  countries: CountryWithAirports[];
  customLegendMessage: string;
  filterOutPartner: boolean;
  formWrapper: HTMLElement;
  groupedAirports: JbAutocompleteOptionsGroup[];
  hideLegend: boolean;
  isMobile: boolean;
  label: string;
  placeholder: string;
  regions: TypedDictionary<Region>;
  separatePartnerCities: boolean;

  isFirstScrollTriggered$: BehaviorSubject<boolean> = new BehaviorSubject(
    false,
  );

  @ViewChild('dlg') jbDialog: JbDialogComponent;
  @ViewChild('dialogContent') dialogContent: JbDialogContentComponent;
  @ViewChild('regionSelector', { static: false })
  regionSelector: RegionSelectorV2Component;

  constructor(
    @Inject(WINDOW) private window,
    private router: Router,
  ) {}

  handleData = (
    content: any,
    control: UntypedFormControl | AbstractControl,
    regions: TypedDictionary<Region>,
    countries: CountryWithAirports[],
    citySelector: any,
    formWrapper: any,
    filterOutPartner: boolean,
    hideLegend: boolean,
    customLegendMessage: string,
    label: string,
    placeholder: string,
    isMobile: boolean,
    groupedAirports: JbAutocompleteOptionsGroup[],
    separatePartnerCities: boolean,
  ) => {
    this.content = content;
    this.control = control;
    this.regions = regions;
    this.countries = countries;
    this.citySelector = citySelector;
    this.formWrapper = formWrapper;
    this.filterOutPartner = filterOutPartner;
    this.hideLegend = hideLegend;
    this.customLegendMessage = customLegendMessage;
    this.label = label;
    this.placeholder = placeholder;
    this.isMobile = isMobile;
    this.groupedAirports = groupedAirports;
    this.separatePartnerCities = separatePartnerCities;
  };

  ngOnInit() {
    // Close the modal if navigation happens.
    this.router.events
      .pipe(
        takeUntilDestroyed(this._destroy),
        filter(event => event instanceof NavigationStart),
      )
      .subscribe(event => {
        this.jbDialog.handleCloseModal(event);
      });
  }

  ngAfterViewInit() {
    if (this.isMobile) {
      /*
      Only start listening the input blur when the user scrolled at least one time in the dialog.
      Doing this, we are avoind blur on the first render.
      */
      merge(
        fromEvent(this.dialogContent.el.nativeElement.parentElement, 'scroll'),
        fromEvent(document, 'scroll'),
      )
        .pipe(take(1))
        .subscribe(() => {
          this.isFirstScrollTriggered$.next(true);
        });
      // only want to subscribe to scrolling events when input has focus and the scroll has been triggered at least once.
      // mobile autofocus on input happens before inputFocused subscription is made
      // so need to manually assume this first emission using concat with of(true).
      // In the event the input hasnt been focused, the extra subscription will
      // be completed at the first occurance of a focusout
      combineLatest([
        this.isFirstScrollTriggered$,
        concat(of(true), this.regionSelector.inputFocused),
      ])
        .pipe(
          takeUntilDestroyed(this._destroy),
          filter(() => this.isFirstScrollTriggered$.value),
        )
        .subscribe(() => {
          this.subToScrollEvents();
        });
    }
  }

  subToScrollEvents() {
    // subscribe to scroll events from either the content container or the document itself
    // because dev testing has shown that somehow document scrolling is still triggered in cases.
    // Logic here is the same as the `dotBlurOnScroll` directive, but it just couldnt but used here
    // because of the focus input and the scrolling parent being spread across two different components
    // and templates
    merge(
      fromEvent(this.dialogContent.el.nativeElement.parentElement, 'scroll'),
      fromEvent(document, 'scroll'),
    )
      .pipe(
        /*
          Only blur if dialogContent offsetHeight is bigger than window innerHeight. Or if the value from document scroll is bigger than 0.
          Case window innerHeight is bigger than dialogContent offsetHeight. That means the scroll is not being used.
          Case documentScroll is bigger than 3, that means we scrolled the document instead of the dialog.
          In this case, it is necessary to blur the input to make the keyboard disappear.
         */
        filter(({ target }, documentScroll) => {
          return (
            target.offsetHeight > this.window.innerHeight || documentScroll > 3
          );
        }),
        takeUntilDestroyed(this._destroy),
        takeUntil(
          fromEvent(this.regionSelector.countryField.nativeElement, 'focusout'),
        ),
      )
      .subscribe(() => {
        // blurring will trigger focusout and complete this subscription
        this.regionSelector.countryField.nativeElement.blur();
      });
  }
}
